<script lang="ts">
    import { getContext, onMount } from 'svelte'
    import { SvelteSet } from 'svelte/reactivity'
    import { derived as sderived } from 'svelte/store'

    import type { GenOrder } from '../../order'
    import Icon from '@iconify/svelte'
    import { DateTime } from 'luxon'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const Order = getContext('Order') as GenOrder
    const status = sderived(Order.status, ($status) => $status)
    const items = sderived(Order.items, ($items) => $items)
    const order = sderived(Order.orderStore, ($order) => $order)

    let inputElement: HTMLInputElement | undefined = $state()
    let value: string = $state('')
    let working = $state(false)

    interface IDaysDiff {
        months?: number
        days?: number
        years?: number
    }

    let daysDiff: IDaysDiff | null = $state(null)

    const possibleDaysDiffs: [IDaysDiff | null, string][] = [
        [{ months: 1 }, '1m'],
        [{ months: 3 }, '3m'],
        [{ years: 1 }, '1y'],
        [null, '∞'],
    ]

    let sortBy: 'domain' | 'expiration' = $state('expiration')
    let sortAsc: boolean = $state(true)

    let results: any[] = $state([])
    let selectedIdnas: SvelteSet<string> = $state(new SvelteSet())

    let domainsInOrder: Set<string> = $derived(new Set($status == 'ready' ? $items.filter((i) => i.type == 'DOMAIN' || i.type == 'RENEWAL').map((i) => i.meta.a_name) : []))

    let existingDomains = sderived(Order.existingDomains, ($v) => $v)

    onMount(() => {
        if (inputElement) inputElement.focus()
    })

    function toggleSelection(item: any) {
        if (selectedIdnas.has(item.idna)) selectedIdnas.delete(item.idna)
        else selectedIdnas.add(item.idna)
    }

    function selectNothing() {
        selectedIdnas = new SvelteSet()
    }

    function selectAll() {
        selectedIdnas = new SvelteSet(results.filter((i: any) => !domainsInOrder.has(i.idna)).map((i: any) => i.idna))
    }

    async function filter() {
        working = true

        results = []

        const sdata = {
            skip: 0,
            limit: 2000,
            owner: $order.owner,
            text_match_style: 'prefix',
            sort_by: sortBy,
            sort_asc: sortAsc,
            text: value,
            expiration_upper: !daysDiff ? null : DateTime.now().plus(daysDiff).toFormat('yyyy-MM-dd'),
        }

        const litems: any = []
        const lidnas: Set<string> = new Set()
        try {
            const r = await simpleFetch('GET', '/json/domain/1/', { data: JSON.stringify(sdata) })
            for (const item of r.data.items) {
                if (item.expiration) item.expiration = toLuxon(item.expiration)
                if (item.zone_expiration != null) item.zone_expiration = toLuxon(item.zone_expiration)

                litems.push(item)
                lidnas.add(item.idna)
            }
            results = litems

            selectedIdnas = new SvelteSet([...selectedIdnas].filter((idna) => lidnas.has(idna)))
        } catch (e) {
            console.error('Lookup failure:', e)
            notify({
                type: 'danger',
                title: $_('Lookup'),
                text: 'La recherche de domaines a généré une erreur.',
            })
        }

        working = false
    }

    let adding = $state(false)
    async function addRenewals() {
        adding = true

        const data = {
            domain_list: [...selectedIdnas],
            n_years: 1,
            renewal: true,
        }
        try {
            const r = await simpleFetch('PUT', `/json/ecom/1/order/${$order.id}/domains`, data)
            selectedIdnas = new SvelteSet()
            Order.loadGenericResponse(r.data)
        } catch (e) {
            console.error('Failed to add renewals:', e)
            notify({
                type: 'danger',
                title: 'Commande',
                text: 'Les domaines n’ont pas pu être ajouté à la commande.',
            })
        }
        adding = false
    }
</script>

<div id="order-domain-lookup">
    <form
        onsubmit={(e) => {
            e.preventDefault()
            filter()
        }}
        style="display: flex; gap: 0.75em; align-items: baseline;"
    >
        <label for="order-add-renew">{$_('Recherche:')}</label>
        <input type="text" class="form-control" id="order-add-renew" style="flex: 1 0 0;" bind:this={inputElement} bind:value disabled={working} />
        <div class="btn-group" style="align-self: center;">
            {#each possibleDaysDiffs as [days, text]}
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    class:active={daysDiff == days}
                    onclick={() => {
                        daysDiff = days
                        filter()
                    }}>{text}</button
                >
            {/each}
        </div>
        <div class="btn-group" style="align-self: center">
            <button
                type="button"
                class="btn btn-outline-secondary"
                class:active={sortBy == 'domain'}
                onclick={() => {
                    sortBy = 'domain'
                    filter()
                }}><Icon inline icon="fa:globe" /></button
            >
            <button
                type="button"
                class="btn btn-outline-secondary"
                class:active={sortBy == 'expiration'}
                onclick={() => {
                    sortBy = 'expiration'
                    filter()
                }}><Icon inline icon="fa:clock-o" /></button
            >
        </div>
        <div class="btn-group" style="align-self: center">
            <button
                type="button"
                class="btn btn-outline-secondary"
                class:active={sortAsc}
                onclick={() => {
                    sortAsc = true
                    filter()
                }}><Icon inline icon="fa:sort-amount-asc" /></button
            >
            <button
                type="button"
                class="btn btn-outline-secondary"
                class:active={!sortAsc}
                onclick={() => {
                    sortAsc = false
                    filter()
                }}><Icon inline icon="fa:sort-amount-desc" /></button
            >
        </div>
        <button type="submit" class="btn btn-primary" disabled={working || $status != 'ready'}><Icon inline icon="fa:search" /></button>
    </form>

    <div class="renewal-list-wrapper">
        <div class="renewal-order">
            <table class="table">
                <thead>
                    <tr>
                        <th style="width: 39px;"></th>
                        <th class="renewal-checkbox"></th>
                        <th>{$_('Domain')}</th>
                        <th class="text-center">{$_('Expiration')}</th>
                        <th class="text-center">Exp. manuelle</th>
                    </tr>
                </thead>
                {#if working}
                    <tbody>
                        <tr><td colspan={5}><LoadingPattern pattern={['x']} /></td></tr>
                        <tr><td colspan={5}><LoadingPattern pattern={['x']} /></td></tr>
                        <tr><td colspan={5}><LoadingPattern pattern={['x']} /></td></tr>
                    </tbody>
                {:else if !results.length}
                    <tbody>
                        <tr>
                            <td colspan={5} class="text-center">Aucun résultat</td>
                        </tr>
                    </tbody>
                {:else}
                    <tbody>
                        {#each results as item}
                            <tr>
                                <td>
                                    {#if $existingDomains[item.idna]}
                                        <a use:sanchor href={spath(`/ecom/orders?q=${toUnicode(item.idna)}`)} class="text-danger tt-right" data-tooltip={$ngettext('Existe dans {n} autre commande ouverte', 'Existe dans {n} autres commandes ouvertes', { n: $existingDomains[item.idna] })}><Icon inline icon="fa:exclamation-triangle" /></a>
                                    {/if}
                                </td>
                                <td class="renewal-checkbox">
                                    <input type="checkbox" disabled={domainsInOrder.has(item.idna)} checked={selectedIdnas.has(item.idna)} onclick={() => toggleSelection(item)} />
                                </td>
                                <td>
                                    <a use:sanchor href={spath(`/domain/${item.idna}`)}>{toUnicode(item.idna)}</a>
                                    {#if item.tags && item.tags.length}
                                        {#each item.tags as tag}
                                            <span class="ms-2 badge text-bg-secondary">{tag}</span>
                                        {/each}
                                    {/if}
                                </td>
                                <td class="text-center"><Date value={item.expiration} format="h-table-s" /></td>
                                <td class="text-center"
                                    >{#if item.special && item.special.expiration}<Date value={item.special.expiration} format="h-table-s" />{/if}</td
                                >
                            </tr>
                        {/each}
                    </tbody>
                {/if}
            </table>
            {#if results.length > 6}
                <div class="gradient-bottom" style="right: 20px;"></div>
            {/if}
        </div>
    </div>
    <div class="renewal-actions">
        <button type="button" class="btn btn-warning float-end" disabled={!selectedIdnas.size || working || !!adding} onclick={() => addRenewals()}>{$ngettext('Ajouter {n} domaine', 'Ajouter {n} domaines', { n: selectedIdnas.size })}</button>
        <div class="btn-group">
            <button type="button" class="btn btn-outline-secondary" disabled>Sélectionner :</button>
            <button type="button" class="btn btn-outline-secondary" disabled={working || !!adding || selectedIdnas.size == results.length} onclick={() => selectAll()}>tout</button>
            <button type="button" class="btn btn-outline-secondary" disabled={working || !!adding || !selectedIdnas.size} onclick={() => selectNothing()}>rien</button>
        </div>
    </div>
</div>
